import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Checkbox,
  Container,
  Paper,
} from "@mui/material";
import { useAuthContext } from "../contexts/AuthContext";
import { useAuth } from "../hooks/useAuth";
import { AuthLayout } from "../components/Layouts";
import { createLoginStyles } from "./styles";
import { useTheme } from "@mui/material/styles";
import { DEFAULT_REDIRECT_PATH, ROUTES } from "../constants/routes";

interface LocationState {
  from?: {
    pathname: string;
  };
  message?: string;
}

/**
 * Login Page Component
 *
 * Handles user authentication using a basic login form.
 * Redirects to the originally requested page after successful login,
 * or to the home page if no specific page was requested.
 */
const Login: React.FC = () => {
  // Styles
  const theme = useTheme();
  const loginStyles = createLoginStyles(theme);

  // Hooks
  const { isAuthenticated } = useAuthContext();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [remember, setRemember] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Get the originally requested location and success message
  const from =
    (location.state as LocationState)?.from?.pathname || DEFAULT_REDIRECT_PATH;
  const messageFromState = (location.state as LocationState)?.message || "";

  // Set success message from navigation state
  useEffect(() => {
    if (messageFromState) {
      setSuccessMessage(messageFromState);
      // Clear the message from navigation state to prevent showing it again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [messageFromState]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Handle login form submission
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setIsLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle forgot password click
  const handleForgotPassword = () => {
    navigate(ROUTES.FORGOT_PASSWORD);
  };

  // If already authenticated, redirect
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  // Render
  return (
    <AuthLayout>
      <title>Login to the Materiom Commons</title>
      <Container sx={loginStyles.container}>
        <Paper elevation={24} sx={loginStyles.paper}>
          {/* Header */}
          <Typography variant="h4" component="h2" sx={loginStyles.header}>
            Get Started
          </Typography>

          <Typography variant="body1" sx={loginStyles.subtitle}>
            Don't have an account?{" "}
            <Link href={ROUTES.REGISTER} sx={loginStyles.registerLink}>
              register
            </Link>
          </Typography>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={loginStyles.errorAlert}>
              {error}
            </Alert>
          )}

          {/* Success Message */}
          {successMessage && (
            <Alert severity="success" sx={loginStyles.errorAlert}>
              {successMessage}
            </Alert>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleLoginSubmit}>
            {/* Email Input */}
            <Box sx={loginStyles.inputContainer}>
              <Typography
                variant="body2"
                component="label"
                sx={loginStyles.fieldLabel}
              >
                Email
              </Typography>
              <TextField
                fullWidth
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                autoFocus
                variant="outlined"
                sx={loginStyles.textField}
              />
            </Box>

            {/* Password Input */}
            <Box sx={loginStyles.inputContainer}>
              <Typography
                variant="body2"
                component="label"
                sx={loginStyles.fieldLabel}
              >
                Password
              </Typography>
              <TextField
                fullWidth
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                variant="outlined"
                sx={loginStyles.textField}
              />
            </Box>

            {/* Remember Me Checkbox */}
            <Box sx={loginStyles.checkboxContainer}>
              <Checkbox
                checked={remember}
                onChange={(e) => {
                  setRemember(e.target.checked);
                  // TODO: Implement remember me functionality
                }}
                sx={loginStyles.checkbox}
                disableRipple
              />
              <Typography
                variant="body2"
                component="label"
                sx={loginStyles.rememberMeLabel}
                onClick={() => setRemember(!remember)}
              >
                Remember me
              </Typography>
            </Box>

            {/* Login Button */}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={loginStyles.loginButton}
              disableRipple
            >
              {isLoading ? "Logging in..." : "Log in"}
            </Button>

            {/* Forgot Password Link */}
            <Box sx={loginStyles.forgotPasswordContainer}>
              <Link
                component="button"
                type="button"
                onClick={handleForgotPassword}
                sx={loginStyles.forgotPasswordLink}
              >
                Forgot your password?
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </AuthLayout>
  );
};

export default Login;
